import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/contexts/AuthContext'
import { getUserUnits, getUserUnitsByContext } from '@/modules/units'
import { OnboardingWizard } from '@/shared/components/onboarding/OnboardingWizard'
import { BillingOnboardingWizard } from '@/modules/billing/onboarding/BillingOnboardingWizard'
import { PricingOnboardingWizard } from '@/modules/pricing/onboarding/PricingOnboardingWizard'
import { PoultryOnboardingWizard } from '@/modules/poultry/onboarding//PoultryOnboardingWizard'
import { AccountingOnboardingWizard } from '@/modules/accounting/onboarding/AccountingOnboardingWizard'
import { appConfig, routes, getContextDashboardRoute } from '@/lib/appConfig'

function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando...</p>
      </div>
    </div>
  )
}

export default function Onboarding() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [userId, setUserId] = useState<string | null>(null)
  const { user, loading: authLoading } = useAuth()

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      navigate(routes.login)
      return
    }

    const check = async () => {
      try {
        // For fixed-context apps, check if user has units for that specific context.
        // This prevents billing users from skipping pricing onboarding (and vice-versa).
        let hasUnits = false
        if (appConfig.fixedContext && appConfig.fixedContext !== 'unit') {
          const contextUnits = await getUserUnitsByContext(user.id, appConfig.fixedContext)
          hasUnits = contextUnits.length > 0
        } else {
          const units = await getUserUnits(user.id)
          hasUnits = units && units.length > 0
        }

        // If user already has a workspace for this context, skip onboarding
        if (hasUnits) {
          navigate(getContextDashboardRoute(appConfig.fixedContext), { replace: true })
          return
        }
      } catch (error) {
        console.error('Error checking units:', error)
      }
      
      setUserId(user.id)
      setIsLoading(false)
    }

    check()
  }, [user, authLoading, navigate])

  if (isLoading || !userId) {
    return <LoadingSpinner />
  }

  // Use billing-specific onboarding if fixed context is billing
  if (appConfig.fixedContext === 'billing') {
    return <BillingOnboardingWizard userId={userId} />
  }

  // Use pricing-specific onboarding if fixed context is pricing
  if (appConfig.fixedContext === 'pricing') {
    return <PricingOnboardingWizard userId={userId} />
  }

  // Use poultry-specific onboarding if fixed context is poultry
  if (appConfig.fixedContext === 'poultry') {
    return <PoultryOnboardingWizard userId={userId} />
  }

  if (appConfig.fixedContext === 'accounting') {
    return <AccountingOnboardingWizard userId={userId} />
  }

  return <OnboardingWizard userId={userId} />
}
